<template>
  <div class="task-list-table fbc-table-header-labels" data-testid="setup-tasklist__table">
    <lb-datatableaction
      ref="dataTableAction"
      :headers="displayHeaders"
      :tabledata="tableData"
      :filter-options-data="filterOptionsData"
      :displayed-row-count="rows.length"
      :itemsperpage="itemsPerPage"
      :bulk-actions="true"
      :is-data-selected="isPartialSelection"
      :checkalldata="isAllSelected"
      :selectfilter="activeColumnFilters"
      :page_details="pageDetails"
      :loading="loading"
      data-testid="setup-tasklist__datatable"
      header-bg-class="fbc-table-header-bg"
      :sticky-column-count="stickyColumnCount"
      @select-all-change="onSelectAllChange"
      @filter-item="onFilterItem"
      @filter-active-change="onFilterActiveChange"
      @clear-item="onClearItem"
      @column-menu-change="onColumnMenuChange"
      @restore-sticky-column-classes="applyStickyColumnClasses"
    >
      <template #tbody>
        <tr
          v-for="item in rows"
          :key="String(item._id)"
          class="task-list-table__row"
          :class="{ 'task-list-table__row--disabled': !item._enabled }"
          data-testid="setup-tasklist__row"
          tabindex="0"
          role="link"
          @click="onRowClick(item, $event)"
          @keydown.enter.prevent="emitRowClick(item)"
          @keydown.space.prevent="emitRowClick(item)"
        >
          <td
            class="px-4"
            data-testid="setup-tasklist__select-cell"
            data-row-nav-ignore
            @click.stop
          >
            <v-checkbox
              :input-value="isSelected(item._id)"
              hide-details
              dense
              color="caccounting"
              :ripple="false"
              class="ma-0 py-1"
              data-testid="setup-tasklist__row-checkbox"
              @change="toggleSelect(item._id, $event)"
            />
          </td>

          <template v-for="header in displayHeaders">
            <td
              v-if="header.visible && header.value === 'description'"
              :key="`${header.value}-${item._id}-description`"
              class="px-4 single-line description-header"
              data-testid="setup-tasklist__task-name-cell"
              @click.stop="emitRowClick(item)"
            >
              <span
                class="task-list-table__truncate"
                data-testid="setup-tasklist__task-name"
              >
                {{ item.description }}
              </span>
            </td>

            <td
              v-else-if="header.visible && header.value === 'locationDisplay'"
              :key="`${header.value}-${item._id}-location`"
              class="px-4 single-line"
              data-testid="setup-tasklist__location-cell"
              @click.stop="emitRowClick(item)"
            >
              <div class="task-list-table__overflow-cell">
                <OverflowList
                  :items="overflowListItems(item.locationPrimary, item.locationOverflowItems)"
                  fixed
                  content-class="task-list-table-fixed-tooltip"
                  data-testid="setup-tasklist__location-overflow"
                />
              </div>
            </td>

            <td
              v-else-if="header.visible && header.value === 'frequencyDisplay'"
              :key="`${header.value}-${item._id}-frequency`"
              class="px-4 single-line"
              data-testid="setup-tasklist__frequency-cell"
              @click.stop="emitRowClick(item)"
            >
              <div class="task-list-table__overflow-cell">
                <OverflowList
                  :items="overflowListItems(item.frequencyPrimary, item.frequencyOverflowItems)"
                  fixed
                  content-class="task-list-table-fixed-tooltip"
                  data-testid="setup-tasklist__frequency-overflow"
                />
              </div>
            </td>

            <td
              v-else-if="header.visible && header.value === 'action'"
              :key="`${header.value}-${item._id}-action`"
              class="single-line border-right border-left action-data"
              data-testid="setup-tasklist__action-cell"
              data-row-nav-ignore
              @click.stop
            >
              <div class="task-list-table__action-group">
                <v-tooltip bottom content-class="tooltip-bottom">
                  <template #activator="{ on, attrs }">
                    <v-btn
                      v-bind="attrs"
                      v-on="on"
                      icon
                      small
                      :disabled="!item._enabled"
                      data-testid="setup-tasklist__edit-btn"
                      @click="$emit('edit', { _id: item._id, task_type: item.task_type })"
                    >
                      <img src="/img/icons/PenEditIcon.svg" alt="Edit Icon" />
                    </v-btn>
                  </template>
                  <span>Edit</span>
                </v-tooltip>

                <v-tooltip bottom content-class="tooltip-bottom">
                  <template #activator="{ on, attrs }">
                    <v-btn
                      v-bind="attrs"
                      v-on="on"
                      icon
                      small
                      data-testid="setup-tasklist__row-activity-log-btn"
                      @click.stop="openRowActivityLog(item)"
                    >
                      <img
                        class="task-list-table__action-icon"
                        src="/img/icons/activity-log.svg"
                        alt="Activity Log"
                      />
                    </v-btn>
                  </template>
                  <span>Activity Log</span>
                </v-tooltip>

                <v-tooltip bottom content-class="tooltip-bottom">
                  <template #activator="{ on, attrs }">
                    <div v-bind="attrs" v-on="on">
                      <v-switch
                        class="task-list-table__switch ml-1"
                        dense
                        inset
                        hide-details
                        :input-value="!!item._enabled"
                        :data-testid="item._enabled ? 'setup-tasklist__toggle-status-enabled' : 'setup-tasklist__toggle-status-disabled'"
                        @change="$emit('toggle-status', { item, enabled: $event })"
                      />
                    </div>
                  </template>
                  <span>{{ item._enabled ? 'Disable' : 'Enable' }}</span>
                </v-tooltip>
              </div>
            </td>

            <td
              v-else-if="header.visible"
              :key="`${header.value}-${item._id}-value`"
              class="px-4 single-line"
              data-testid="setup-tasklist__cell"
              @click.stop="emitRowClick(item)"
            >
              {{ getCellValue(item, header.value) }}
            </td>
          </template>
        </tr>
      </template>
    </lb-datatableaction>

    <ActivityLog
      ref="rowActivityLog"
      hide-trigger
      :resource-id="rowActivityLogId"
      :row-description="rowActivityLogDescription"
      data-testid="setup-tasklist__row-activity-log"
    />
  </div>
</template>

<script>
import './TaskListTable.scss';
import { ActivityLog, OverflowList } from '@/modules/financialclose/components';
import { PAGE_DETAILS } from '../../utils/taskListConstants';

export default {
  name: 'TaskListTable',

  components: {
    ActivityLog,
    OverflowList,
  },

  props: {
    rows: {
      type: Array,
      default: () => [],
    },
    tableData: {
      type: Array,
      default: () => [],
    },
    filterOptionsData: {
      type: Array,
      default: null,
    },
    headers: {
      type: Array,
      default: () => [],
    },
    selectedIds: {
      type: Array,
      default: () => [],
    },
    itemsPerPage: {
      type: Number,
      default: 25,
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      activeColumnFilters: {},
      pageDetails: { ...PAGE_DETAILS },
      stickyColumnCount: 3,
      stickyUpdateScheduled: false,
      stickyRetryTimeoutId: null,
      isDestroyed: false,
      headerDragging: false,
      columnMenuOpen: false,
      rowActivityLogId: '',
      rowActivityLogDescription: '',
    };
  },

  watch: {
    rows() {
      this.scheduleStickyUpdate();
    },
    displayHeaders() {
      this.scheduleStickyUpdate();
    },
    loading() {
      this.scheduleStickyUpdate();
    },
  },

  mounted() {
    this.scheduleStickyUpdate();
  },

  updated() {
    if (this.columnMenuOpen) return;
    this.scheduleStickyUpdate();
  },

  beforeDestroy() {
    this.isDestroyed = true;
    this.clearStickyRetryTimeout();
  },

  computed: {
    /** Drop custom select column — lb-datatableaction owns the select-all header. */
    displayHeaders() {
      return (this.headers || []).filter((h) => h?.value !== 'select');
    },

    isPartialSelection() {
      const source = this.tableData || [];
      const ids = this.selectedIds || [];
      if (!ids.length || !source.length || this.isAllSelected) return false;
      const selectedSet = new Set(ids);
      return source.some((row) => selectedSet.has(row._id));
    },

    isAllSelected() {
      const source = this.tableData || [];
      const ids = this.selectedIds || [];
      if (!ids.length || !source.length) return false;
      if (ids.length < source.length) return false;
      const selectedSet = new Set(ids);
      return source.every((row) => row?._id && selectedSet.has(row._id));
    },
  },

  methods: {
    overflowListItems(primary, overflowItems) {
      const values = [primary, ...(Array.isArray(overflowItems) ? overflowItems : [])];
      return values.filter((value) => {
        const text = value == null ? '' : String(value).trim();
        return text && text !== '—';
      });
    },

    getCellValue(item, key) {
      const val = item[key];
      if (val == null || val === '') return '—';
      return val;
    },

    isSelected(id) {
      return (this.selectedIds || []).includes(id);
    },

    toggleSelect(id, checked) {
      const next = new Set(this.selectedIds || []);
      if (checked) next.add(id);
      else next.delete(id);
      this.$emit('update:selectedIds', Array.from(next));
      this.$emit('selection-change', Array.from(next));
    },

    onSelectAllChange(checked) {
      if (checked) {
        const ids = (this.tableData || []).map((r) => r._id).filter(Boolean);
        this.$emit('update:selectedIds', ids);
        this.$emit('selection-change', ids);
      } else {
        this.$emit('update:selectedIds', []);
        this.$emit('selection-change', []);
      }
    },

    onRowClick(item, event) {
      const target = event?.target;
      if (target && typeof target.closest === 'function') {
        // Ignore clicks from checkbox / action controls (and their wrappers).
        if (target.closest('[data-row-nav-ignore], button, a, .v-input, .v-checkbox, .v-dialog')) {
          return;
        }
      }
      this.emitRowClick(item);
    },

    emitRowClick(item) {
      if (!item?._id) return;
      this.$emit('row-click', item);
    },

    openRowActivityLog(item) {
      if (!item?._id) return;
      this.rowActivityLogId = String(item._id);
      this.rowActivityLogDescription = item.description || '';
      this.$nextTick(() => {
        this.$refs.rowActivityLog?.openDialog();
      });
    },

    onFilterItem(filters) {
      this.activeColumnFilters = filters || {};
      this.$emit('filter-change', this.activeColumnFilters);
    },

    onFilterActiveChange(active) {
      this.$emit('filter-active-change', active);
    },

    onClearItem() {
      this.activeColumnFilters = {};
      this.$emit('filter-change', {});
    },

    clearTableFilter() {
      this.activeColumnFilters = {};
      if (this.$refs.dataTableAction?.clearFilter) {
        this.$refs.dataTableAction.clearFilter();
      }
      this.$emit('filter-change', {});
    },

    /** Keep header select-all checkbox in sync after external selection clears. */
    syncHeaderCheckbox() {
      const tableRef = this.$refs.dataTableAction;
      if (tableRef) {
        tableRef.selectAll = this.isAllSelected;
      }
      this.scheduleStickyUpdate();
    },

    onColumnMenuChange(isOpen) {
      this.columnMenuOpen = !!isOpen;
      if (isOpen) {
        this.$nextTick(() => {
          this.applyStickyColumnClasses();
        });
        return;
      }
      this.$nextTick(() => {
        setTimeout(() => this.scheduleStickyUpdate(), 50);
      });
    },

    getStickyTableElements() {
      const table = this.$el?.querySelector('.v-data-table__wrapper > table');
      if (!table) return null;
      const headerRow = table.querySelector('thead tr');
      if (!headerRow) return null;
      return { table, headerRow };
    },

    getStickyHeaderTargets(headerRow) {
      const count = Number(this.stickyColumnCount || 3);
      const visible = Array.from(headerRow.children).filter((th) => {
        const rect = th.getBoundingClientRect();
        return th.offsetParent !== null && rect.width > 0.5;
      });
      if (!visible.length) return null;
      return { count, targets: visible.slice(0, count) };
    },

    restoreStickyColumnClassesFromDataset(table) {
      const count = Number(this.stickyColumnCount || 3);
      const edgeSeq = String(count - 1);
      table.querySelectorAll('thead th[data-sticky-seq]').forEach((th) => {
        th.classList.add('dyn-sticky-col');
        th.classList.toggle('dyn-sticky-col-edge', th.dataset.stickySeq === edgeSeq);
      });
      table.querySelectorAll('tbody td[data-sticky-seq]').forEach((td) => {
        td.classList.add('dyn-sticky-col');
        td.classList.toggle('dyn-sticky-col-edge', td.dataset.stickySeq === edgeSeq);
      });
    },

    applyStickyColumnClasses() {
      try {
        if (this.isDestroyed) return;
        const elements = this.getStickyTableElements();
        if (!elements) return;
        const { table, headerRow } = elements;
        const targetInfo = this.getStickyHeaderTargets(headerRow);
        if (!targetInfo) {
          this.restoreStickyColumnClassesFromDataset(table);
          return;
        }

        const { targets } = targetInfo;
        const headerIndexes = [];
        const widths = [];
        let left = 0;

        targets.forEach((th, seq) => {
          let w = th.getBoundingClientRect().width;
          if (!w || w < 1) {
            w = th.offsetWidth || 80;
          }
          headerIndexes.push(Array.from(headerRow.children).indexOf(th));
          widths.push(w);
          th.classList.add('dyn-sticky-col');
          th.dataset.stickySeq = String(seq);
          th.style.left = `${left}px`;
          th.style.width = `${w}px`;
          th.style.minWidth = `${w}px`;
          th.classList.toggle('dyn-sticky-col-edge', seq === targets.length - 1);
          left += w;
        });

        const bodyRows = table.querySelectorAll('tbody tr');
        bodyRows.forEach((tr) => {
          let running = 0;
          headerIndexes.forEach((idx, seq) => {
            const cell = tr.children[idx];
            if (!cell) return;
            cell.classList.add('dyn-sticky-col');
            cell.dataset.stickySeq = String(seq);
            cell.style.left = `${running}px`;
            const w = widths[seq];
            if (w) {
              cell.style.width = `${w}px`;
              cell.style.minWidth = `${w}px`;
            }
            cell.classList.toggle('dyn-sticky-col-edge', seq === headerIndexes.length - 1);
            running += widths[seq];
          });
        });
      } catch (_) {
        /* silent */
      }
    },

    scheduleStickyUpdate() {
      if (this.stickyUpdateScheduled || this.isDestroyed || this.columnMenuOpen) return;
      this.stickyUpdateScheduled = true;
      requestAnimationFrame(() => {
        this.stickyUpdateScheduled = false;
        if (this.isDestroyed) return;
        this.updateStickyOffsets();
      });
    },

    clearStickyRetryTimeout() {
      if (this.stickyRetryTimeoutId != null) {
        clearTimeout(this.stickyRetryTimeoutId);
        this.stickyRetryTimeoutId = null;
      }
    },

    scheduleStickyRetry() {
      this.clearStickyRetryTimeout();
      if (this.isDestroyed) return;
      this.stickyRetryTimeoutId = setTimeout(() => {
        this.stickyRetryTimeoutId = null;
        if (this.isDestroyed) return;
        this.updateStickyOffsets();
      }, 100);
    },

    updateStickyOffsets() {
      try {
        if (this.isDestroyed || this.headerDragging || this.columnMenuOpen) return;
        const elements = this.getStickyTableElements();
        if (!elements) {
          this.scheduleStickyRetry();
          return;
        }
        const { table, headerRow } = elements;
        const targetInfo = this.getStickyHeaderTargets(headerRow);
        if (!targetInfo) {
          this.scheduleStickyRetry();
          return;
        }

        table.querySelectorAll('th.dyn-sticky-col, td.dyn-sticky-col').forEach((el) => {
          el.classList.remove('dyn-sticky-col', 'dyn-sticky-col-edge');
          el.style.left = '';
          el.style.width = '';
          el.style.minWidth = '';
          el.removeAttribute('data-sticky-seq');
        });
        this.applyStickyColumnClasses();
      } catch (_) {
        /* silent */
      }
    },
  },
};
</script>
